import { LenderName, LoanStatus, MetricLenderApplicationView, MetricLenderView } from '@nemuru-eng/js-api-client';

import { LenderRankingRow } from '@/components/modules/Metrics/LenderRanking/types/LenderRankingRow';
import { LenderRankingSummaryType } from '@/components/modules/Metrics/LenderRanking/types/LenderRankingSummaryType';
import { TimeInterval } from '@/components/modules/Metrics/shared/entities/TimeInterval';
import { ScoreCardMetricKey } from '@/components/modules/Metrics/shared/types/ScoreCardMetricKey';

export const LENDER_RANKING_TOP = 3;

interface LenderTotals {
  lenderId: string;
  lenderName: string;
  principal: number;
  previousPrincipal: number;
  loans: number;
  previousLoans: number;
  formalizationSeconds: number;
  formalizedLoans: number;
  approved: number;
  completed: number;
}

export class LenderRankingMap {
  static toRows(
    lenderViews: MetricLenderView[],
    lenderApplicationViews: MetricLenderApplicationView[],
    timeInterval: TimeInterval,
    metricKey: ScoreCardMetricKey,
  ): { lenders: LenderRankingRow[]; summaries: LenderRankingRow[] } {
    const allLenders = LenderRankingMap.lenderTotals(lenderViews, lenderApplicationViews, timeInterval).sort(
      (a, b) =>
        LenderRankingMap.value(b, metricKey) - LenderRankingMap.value(a, metricKey) || b.completed - a.completed,
    );
    const ranked = allLenders.filter((lender) => lender.loans > 0 || lender.completed > 0);
    const top = ranked.slice(0, LENDER_RANKING_TOP);
    const rest = allLenders.filter((lender) => !top.includes(lender));
    const all = LenderRankingMap.sum(allLenders);
    const total = LenderRankingMap.value(all, metricKey);

    const toRow = (lender: LenderTotals, key: string): LenderRankingRow => ({
      key,
      value: LenderRankingMap.value(lender, metricKey),
      share: total ? LenderRankingMap.value(lender, metricKey) / total : 0,
      variation: LenderRankingMap.variation(lender, metricKey),
      approval: lender.completed ? lender.approved / lender.completed : undefined,
      averageTicket: lender.loans ? lender.principal / lender.loans : undefined,
      averageFormalizationSeconds: lender.formalizedLoans
        ? lender.formalizationSeconds / lender.formalizedLoans
        : undefined,
    });

    return {
      lenders: ranked.map((lender) => ({
        ...toRow(lender, lender.lenderId),
        lenderName: LenderRankingMap.isLenderName(lender.lenderName) ? lender.lenderName : undefined,
        lenderLabel: lender.lenderName,
      })),
      summaries: [
        ...(ranked.length > LENDER_RANKING_TOP
          ? [
              {
                ...toRow(LenderRankingMap.sum(top), LenderRankingSummaryType.TOP),
                summaryType: LenderRankingSummaryType.TOP,
              },
              {
                ...toRow(LenderRankingMap.sum(rest), LenderRankingSummaryType.REST),
                summaryType: LenderRankingSummaryType.REST,
              },
            ]
          : []),
        ...(ranked.length
          ? [{ ...toRow(all, LenderRankingSummaryType.TOTAL), summaryType: LenderRankingSummaryType.TOTAL }]
          : []),
      ],
    };
  }

  private static lenderTotals(
    lenderViews: MetricLenderView[],
    lenderApplicationViews: MetricLenderApplicationView[],
    timeInterval: TimeInterval,
  ): LenderTotals[] {
    const byLender = new Map<string, LenderTotals>();
    const totalsOf = (lenderId: string, lenderName: string): LenderTotals => {
      if (!byLender.has(lenderId)) {
        byLender.set(lenderId, { ...LenderRankingMap.empty(), lenderId, lenderName });
      }
      return byLender.get(lenderId) as LenderTotals;
    };

    lenderViews.forEach((lenderView) => {
      const totals = totalsOf(lenderView.lenderId, lenderView.lenderName);
      lenderView.metrics
        .filter((metric) => metric.loanStatus === LoanStatus.STATUS_DOCS_APPROVED)
        .forEach((metric) =>
          metric.metricsByDate.forEach((day) => {
            if (timeInterval.isCurrent(day.date)) {
              totals.principal += day.totalPrincipalAmount;
              totals.loans += day.totalLoansNumber;
              totals.formalizationSeconds += day.totalFormalizationSeconds;
              totals.formalizedLoans += day.formalizedLoansNumber;
            }
            if (timeInterval.isPrevious(day.date)) {
              totals.previousPrincipal += day.totalPrincipalAmount;
              totals.previousLoans += day.totalLoansNumber;
            }
          }),
        );
    });

    lenderApplicationViews.forEach((lenderApplicationView) => {
      const totals = totalsOf(lenderApplicationView.lenderId, lenderApplicationView.lenderName);
      lenderApplicationView.metricsByDate
        .filter((day) => timeInterval.isCurrent(day.date))
        .forEach((day) => {
          totals.approved += day.approvedNumber;
          totals.completed += day.approvedNumber + day.deniedNumber + day.inStudyNumber + day.errorNumber;
        });
    });

    return [...byLender.values()];
  }

  private static isLenderName(lenderName: string): lenderName is LenderName {
    return Object.values(LenderName).includes(lenderName as LenderName);
  }

  private static value(totals: LenderTotals, metricKey: ScoreCardMetricKey): number {
    return metricKey === ScoreCardMetricKey.totalLoansNumber ? totals.loans : totals.principal;
  }

  private static variation(totals: LenderTotals, metricKey: ScoreCardMetricKey): number | undefined {
    const previous =
      metricKey === ScoreCardMetricKey.totalLoansNumber ? totals.previousLoans : totals.previousPrincipal;
    return previous ? (LenderRankingMap.value(totals, metricKey) - previous) / previous : undefined;
  }

  private static sum(totals: LenderTotals[]): LenderTotals {
    return totals.reduce(
      (sum, lender) => ({
        ...sum,
        principal: sum.principal + lender.principal,
        previousPrincipal: sum.previousPrincipal + lender.previousPrincipal,
        loans: sum.loans + lender.loans,
        previousLoans: sum.previousLoans + lender.previousLoans,
        formalizationSeconds: sum.formalizationSeconds + lender.formalizationSeconds,
        formalizedLoans: sum.formalizedLoans + lender.formalizedLoans,
        approved: sum.approved + lender.approved,
        completed: sum.completed + lender.completed,
      }),
      LenderRankingMap.empty(),
    );
  }

  private static empty(): LenderTotals {
    return {
      lenderId: '',
      lenderName: '',
      principal: 0,
      previousPrincipal: 0,
      loans: 0,
      previousLoans: 0,
      formalizationSeconds: 0,
      formalizedLoans: 0,
      approved: 0,
      completed: 0,
    };
  }
}
